import React from 'react'
import type { GroupBase, SelectInstance } from 'react-select'
import { Controller } from 'react-hook-form'
import { t } from 'ttag'
import { AsyncSelect } from './common/AsyncSelect/AsyncSelect'
import { Select } from './common/Select/Select'
import type { ComboboxProps } from './types'
import type { ControlledFieldProps } from '../../types'

export const Combobox = React.forwardRef<
  SelectInstance<unknown, boolean, GroupBase<unknown>>,
  ComboboxProps
>(
  (
    {
      error,
      id,
      name,
      noOptionsMessage,
      customComponents,
      options,
      placeholder,
      value,
      fetchOptions = null,
      closeMenuOnSelect,
      transformData,
      onBlur,
      filterOption,
      onChange,
      isClearable,
      isCreatable,
      isDisabled,
      isMulti,
      isReadOnly,
      isRequired,
      isSearchable,
      styles,
    },
    ref,
  ) => {
    const shared = {
      error,
      id,
      name,
      noOptionsMessage,
      options,
      placeholder,
      value,
      filterOption,
      closeMenuOnSelect,
      customComponents,
      transformData,
      onBlur,
      onChange,
      isClearable,
      isCreatable,
      isDisabled,
      isMulti,
      isReadOnly,
      isRequired,
      isSearchable,
      ref,
      styles,
    }

    // AsyncSelect requires fetchOptions; Select has no use for it. Branching
    // here (rather than picking a component) keeps both calls typed.
    return fetchOptions == null ? (
      <Select {...shared} />
    ) : (
      <AsyncSelect {...shared} fetchOptions={fetchOptions} />
    )
  },
)

type ControlledComboboxProps = ControlledFieldProps &
  Omit<ComboboxProps, keyof import('../../types').FormFieldBaseProps>

export const ControlledCombobox = ({
  control,
  error,
  id,
  name,
  noOptionsMessage,
  customComponents,
  transformData,
  options,
  closeMenuOnSelect,
  placeholder = `${t`Select`}...`,
  fetchOptions,
  filterOption,
  isClearable,
  isCreatable,
  isDisabled,
  isMulti,
  isReadOnly,
  isRequired,
  isSearchable,
}: ControlledComboboxProps) => (
  <Controller
    render={({ field: { name, ref, value, onBlur, onChange } }) => (
      <Combobox
        {...{
          error,
          id,
          name,
          noOptionsMessage,
          customComponents,
          transformData,
          options,
          placeholder,
          ref,
          filterOption,
          value,
          fetchOptions,
          closeMenuOnSelect,
          onBlur,
          onChange,
          isClearable,
          isCreatable,
          isDisabled,
          isMulti,
          isReadOnly,
          isRequired,
          isSearchable,
        }}
      />
    )}
    {...{ control, name }}
  />
)
